// The ask service's post-forecast insights (deep-harness `postprocess/insights.json`), as the
// report modal shows them. Each finding may cite several evidence cards. The service puts the
// envelope on `structured_explanation.insights`, which the forecast server carries as
// `report.insights`; runs that did not ask for insights, or whose generation was skipped or
// failed, carry none. Distinct from the report's reasons, which the UI also calls insights.

import { citedCardsOf } from "./evidenceCards.js";

const SUPPORTED_SCHEMA = 1;
const text = (v) => (typeof v === "string" && v.trim() ? v.trim() : "");

/** The DOM id a cross-evidence insight carries ("I1"), clear of the reasons' `insight-` ids. */
export const crossInsightAnchor = (ref) => `cross-insight-${ref}`;

/**
 * The report's cross-evidence insights, each with its cited cards resolved; null when there
 * are none to show (absent, not generated, or an unsupported schema version).
 *
 * @param {object|null} report the report object (`r`)
 * @param {Array} cards evidenceCardsOf(report)
 * @returns {Array<{ref: string, text: string, cards: Array, cardIds: string[]}>|null}
 */
export function crossInsightsOf(report, cards) {
  const env = report?.insights;
  if (!env || typeof env !== "object" || Array.isArray(env)) return null;
  if (env.schema_version !== SUPPORTED_SCHEMA || env.status !== "generated") return null;
  if (!Array.isArray(env.insights)) return null;
  const items = env.insights
    .filter((i) => i && typeof i === "object" && text(i.text))
    .map((i, n) => {
      // Trimmed and once each, so a repeated or padded id never reads as a missing card.
      const cardIds = [
        ...new Set((Array.isArray(i.card_ids) ? i.card_ids : []).map(text).filter(Boolean)),
      ];
      return {
        ref: `I${n + 1}`,
        text: text(i.text),
        cards: citedCardsOf(cardIds, cards || []),
        cardIds,
      };
    });
  return items.length ? items : null;
}

const LABEL_MAX = 40;
const TITLE_MAX = 80;

/**
 * The prompt asking for each insight's category and headline: a short label naming the kind
 * of evidence ("Market signal") and a few words stating its point, for the brief's cards.
 * @param {{question: string, insights: Array<{ref: string, text: string}>}} args
 */
export function insightHeadingsPrompt({ question, insights }) {
  return [
    "Each finding below explains part of a forecast. For each, write:",
    '- label: the kind of evidence it rests on, 2 to 4 words in sentence case (e.g. "Market signal", "Historical base rate", "Roster and labor risk").',
    '- title: its point as a headline of at most 8 words, no final period (e.g. "History favors a broad field").',
    "Use only what the finding says: no new facts or figures. Plain words, no markdown or quotation marks.",
    'Return only JSON: {"insights": [{"ref": "I1", "label": "...", "title": "..."}]}, one entry per finding, in order.',
    "",
    `Question: ${question}`,
    "",
    ...insights.map((it) => `${it.ref}: ${it.text}`),
  ].join("\n");
}

/**
 * A model's insight headings, as `{insights: [{ref, label, title}]}`; null unless every entry
 * is a short, plain label and title. The cached value is the same JSON, so it re-reads here.
 * @param {string} text
 */
export function cleanInsightHeadings(text) {
  const raw = String(text || "");
  let parsed;
  try {
    parsed = JSON.parse(raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1));
  } catch {
    return null;
  }
  const items = Array.isArray(parsed?.insights) ? parsed.insights : null;
  if (!items?.length) return null;
  const plain = (v, max) => {
    const t = typeof v === "string" ? v.replace(/\s+/g, " ").trim().replace(/\.$/, "") : "";
    return t && t.length <= max && !/[*#`"]/.test(t) ? t : null;
  };
  const out = [];
  for (const it of items) {
    const ref = typeof it?.ref === "string" ? it.ref.trim() : "";
    const label = plain(it?.label, LABEL_MAX);
    const title = plain(it?.title, TITLE_MAX);
    if (!ref || !label || !title) return null;
    out.push({ ref, label, title });
  }
  return { insights: out };
}

/**
 * An insight split for a card when no heading was written: its first sentence as the title,
 * the rest as the explanation.
 * @param {string} text
 */
export function fallbackHeading(text) {
  const t = String(text || "").trim();
  const m = /^(.+?[.!?])\s+(?=[A-Z0-9"“(])(.*)$/s.exec(t);
  return m ? { title: m[1], body: m[2] } : { title: t, body: "" };
}
